import { Edge, Node } from '@xyflow/react';

import { ConnectionValidationResult } from '../types/connections';
import { NODE_TYPE } from '../types/node';

export type ForLoopConnectionValidationResult = {
  canConnect: boolean;
  reason?: string;
} & ConnectionValidationResult;

export class ForLoopConnectionValidator {
  private nodes: Node[];
  private edges: Edge[];

  constructor(nodes: Node[], edges: Edge[]) {
    this.nodes = nodes;
    this.edges = edges;
  }

  validateForLoopEntry(
    sourceNodeId: string,
    forLoopNodeId: string
  ): ForLoopConnectionValidationResult {
    const forLoopNode = this.nodes.find(n => n.id === forLoopNodeId);
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);

    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Target is not a ForLoop node',
        error: 'Invalid target node type',
      };
    }

    if (!sourceNode) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source node not found',
        error: 'Source node does not exist',
      };
    }

    // Note: Max connections validation is handled by the general validateConnectionCounts()
    // in useConnectionValidation.ts, which respects the maxConnections config from ForLoopNodeType

    if (this.isNodeInsideForLoop(sourceNodeId, forLoopNodeId)) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Cannot connect from inside ForLoop to ForLoop input',
        error: 'Invalid internal to external connection',
      };
    }

    return {
      isValid: true,
      canConnect: true,
    };
  }

  validateForLoopExit(
    forLoopNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const forLoopNode = this.nodes.find(n => n.id === forLoopNodeId);
    const targetNode = this.nodes.find(n => n.id === targetNodeId);

    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source is not a ForLoop node',
        error: 'Invalid source node type',
      };
    }

    if (!targetNode) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Target node not found',
        error: 'Target node does not exist',
      };
    }

    if (this.isNodeInsideForLoop(targetNodeId, forLoopNodeId)) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Cannot connect from ForLoop to its internal nodes',
        error: 'Invalid external to internal connection',
      };
    }

    if (targetNode.type === NODE_TYPE.FOR_LOOP && targetNodeId !== forLoopNodeId) {
      return this.validateForLoopEntry(forLoopNodeId, targetNodeId);
    }

    return {
      isValid: true,
      canConnect: true,
    };
  }

  validateInternalForLoopConnection(
    sourceNodeId: string,
    targetNodeId: string,
    forLoopNodeId: string
  ): ForLoopConnectionValidationResult {
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);
    const targetNode = this.nodes.find(n => n.id === targetNodeId);

    if (!sourceNode || !targetNode) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source or target node not found',
        error: 'Node does not exist',
      };
    }

    const isSourceInternal = this.isNodeInsideForLoop(sourceNodeId, forLoopNodeId);
    const isTargetInternal = this.isNodeInsideForLoop(targetNodeId, forLoopNodeId);

    if (!isSourceInternal || !isTargetInternal) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Both nodes must be inside the same ForLoop',
        error: 'Invalid cross-boundary connection',
      };
    }

    if (this.wouldCreateCycle(sourceNodeId, targetNodeId)) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Connection would create a cycle',
        error: 'Circular dependency detected',
      };
    }

    return {
      isValid: true,
      canConnect: true,
    };
  }

  validateExternalToInternalConnection(
    sourceNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const targetNode = this.nodes.find(n => n.id === targetNodeId);
    if (!targetNode || !targetNode.parentId) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Target is not inside a ForLoop',
        error: 'Invalid connection target',
      };
    }

    const forLoopNode = this.nodes.find(n => n.id === targetNode.parentId);
    if (!forLoopNode || forLoopNode.type !== NODE_TYPE.FOR_LOOP) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Target parent is not a ForLoop',
        error: 'Invalid parent node type',
      };
    }

    const isSourceExternal = !this.isNodeInsideForLoop(sourceNodeId, targetNode.parentId);
    if (!isSourceExternal) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source must be external to ForLoop',
        error: 'Invalid internal connection',
      };
    }

    // Block direct external to internal connections - must go through ForLoop node
    return {
      isValid: false,
      canConnect: false,
      reason: 'Cannot connect directly to nodes inside ForLoop',
      error: 'Connect to the ForLoop node instead',
    };
  }

  validateInternalToExternalConnection(
    sourceNodeId: string,
    targetNodeId: string
  ): ForLoopConnectionValidationResult {
    const sourceNode = this.nodes.find(n => n.id === sourceNodeId);
    if (!sourceNode || !sourceNode.parentId) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source is not inside a ForLoop',
        error: 'Invalid connection source',
      };
    }

    const isTargetExternal = !this.isNodeInsideForLoop(targetNodeId, sourceNode.parentId);
    if (!isTargetExternal) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Target must be external to ForLoop',
        error: 'Invalid internal connection',
      };
    }

    // Block direct internal to external connections - must go through ForLoop node
    return {
      isValid: false,
      canConnect: false,
      reason: 'Cannot connect from inside ForLoop directly to external nodes',
      error: 'Connect from the ForLoop node instead',
    };
  }

  private isNodeInsideForLoop(nodeId: string, forLoopNodeId: string): boolean {
    const node = this.nodes.find(n => n.id === nodeId);
    return node?.parentId === forLoopNodeId;
  }

  private wouldCreateCycle(sourceNodeId: string, targetNodeId: string): boolean {
    const visited = new Set<string>();
    const stack = [targetNodeId];

    while (stack.length > 0) {
      const currentNodeId = stack.pop();
      if (!currentNodeId) continue;

      if (currentNodeId === sourceNodeId) {
        return true;
      }

      if (visited.has(currentNodeId)) {
        continue;
      }

      visited.add(currentNodeId);

      const outgoingEdges = this.edges.filter(edge => edge.source === currentNodeId);
      for (const edge of outgoingEdges) {
        if (!visited.has(edge.target)) {
          stack.push(edge.target);
        }
      }
    }

    return false;
  }

  static validateConnection(
    sourceNodeId: string,
    targetNodeId: string,
    nodes: Node[],
    edges: Edge[]
  ): ForLoopConnectionValidationResult {
    const validator = new ForLoopConnectionValidator(nodes, edges);

    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    const targetNode = nodes.find(n => n.id === targetNodeId);

    if (!sourceNode || !targetNode) {
      return {
        isValid: false,
        canConnect: false,
        reason: 'Source or target node not found',
        error: 'Node does not exist',
      };
    }

    if (sourceNode.type === NODE_TYPE.FOR_LOOP && targetNode.type !== NODE_TYPE.FOR_LOOP) {
      return validator.validateForLoopExit(sourceNodeId, targetNodeId);
    }

    if (sourceNode.type !== NODE_TYPE.FOR_LOOP && targetNode.type === NODE_TYPE.FOR_LOOP) {
      return validator.validateForLoopEntry(sourceNodeId, targetNodeId);
    }

    if (sourceNode.parentId && targetNode.parentId) {
      if (sourceNode.parentId === targetNode.parentId) {
        return validator.validateInternalForLoopConnection(
          sourceNodeId,
          targetNodeId,
          sourceNode.parentId
        );
      }
    }

    if (sourceNode.parentId && !targetNode.parentId) {
      return validator.validateInternalToExternalConnection(sourceNodeId, targetNodeId);
    }

    if (!sourceNode.parentId && targetNode.parentId) {
      return validator.validateExternalToInternalConnection(sourceNodeId, targetNodeId);
    }

    return {
      isValid: true,
      canConnect: true,
    };
  }
}
